import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import Layout from '../components/Layout';
import api from '../api/axios';
import { C } from '../theme';

const STATUS_STYLE = {
  'in-stock':    { bg: '#DCFCE7', color: '#15803D', label: 'In Stock',    icon: '📦', dot: '#16A34A' },
  'packed':      { bg: '#EDE9FE', color: '#6D28D9', label: 'Packed',      icon: '🎁', dot: '#7C3AED' },
  'dispatched':  { bg: '#DBEAFE', color: '#1D4ED8', label: 'Dispatched',  icon: '🚚', dot: '#2563EB' },
  'installed':   { bg: '#D1FAE5', color: '#065F46', label: 'Installed',   icon: '✅', dot: '#059669' },
  'replacement': { bg: '#E0F2FE', color: '#0369A1', label: 'Replacement', icon: '🔄', dot: '#0284C7' },
  'cancelled':   { bg: '#FEE2E2', color: '#991B1B', label: 'Cancelled',   icon: '🚫', dot: '#EF4444' },
  'damaged':     { bg: '#FEF3C7', color: '#92400E', label: 'Damaged',     icon: '⚠️', dot: '#D97706' },
  'expired':     { bg: '#FEF9C3', color: '#713F12', label: 'Expired',     icon: '⏰', dot: '#CA8A04' },
  'not_working': { bg: '#FFE4E6', color: '#9F1239', label: 'Not Working', icon: '🔴', dot: '#E11D48' },
  'lost':        { bg: '#FEE2E2', color: '#991B1B', label: 'Lost',        icon: '❌', dot: '#DC2626' },
  'other':         { bg: '#F3F4F6', color: '#374151', label: 'Other',        icon: '📋', dot: '#6B7280' },
  'deployed':      { bg: '#DBEAFE', color: '#1D4ED8', label: 'Deployed',     icon: '🔧', dot: '#2563EB' },
  'dealer-stock':  { bg: '#F5F3FF', color: '#6D28D9', label: 'Dealer Stock', icon: '🏪', dot: '#7C3AED' },
};

// Derive a context-aware label for the serial number field
function identifierMeta(categoryName = '') {
  const n = categoryName.toLowerCase();
  if (n.includes('sim'))                               return { label: 'SIM / Phone No.',  placeholder: 'e.g. 9876543210' };
  if (n.includes('device') || n.includes('gps') || n.includes('tracker')) return { label: 'IMEI Number', placeholder: 'e.g. 358240051111110' };
  if (n.includes('software') || n.includes('license')) return { label: 'License Key',      placeholder: 'e.g. XXXX-XXXX-XXXX-XXXX' };
  return { label: 'Serial / Identifier', placeholder: 'Serial number' };
}

export default function InventoryCategory() {
  const { categoryId } = useParams();

  const [category,  setCategory]  = useState(null);
  const [items,     setItems]     = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [toast,     setToast]     = useState(null);

  // Modal state
  const [modal,     setModal]     = useState(null);  // 'add' | 'edit' | 'delete'
  const [selItem,   setSelItem]   = useState(null);

  // Add-stock wizard
  const [phase,     setPhase]     = useState(1);     // 1 = PO/type pick, 2 = serials
  const [poStock,   setPoStock]   = useState([]);
  const [selPoId,   setSelPoId]   = useState('');
  const [selEntry,  setSelEntry]  = useState(null);  // { po_item_id, remaining, type_name, po_number, … }
  const [serials,    setSerials]    = useState([]);    // array of strings
  const [refNumbers, setRefNumbers] = useState([]);    // parallel array for reference numbers
  const [isManual,   setIsManual]   = useState(false); // false = scan mode (auto-advance on Enter)
  const [saving,     setSaving]     = useState(false);
  const serialRefs = useRef([]);                       // input refs for focus management

  // Migration stock state
  const [migTypes,    setMigTypes]    = useState([]);
  const [migTypeId,   setMigTypeId]   = useState('');
  const [migQty,      setMigQty]      = useState(1);
  const [migSerials,  setMigSerials]  = useState(['']);
  const [migRefs,     setMigRefs]     = useState(['']);
  const [migSaving,   setMigSaving]   = useState(false);

  // Edit form
  const [editForm,  setEditForm]  = useState({ serial_number: '', ref_number: '', status: 'in-stock', notes: '' });

  // Search / filter
  const [search,    setSearch]    = useState('');
  const [stFilter,  setStFilter]  = useState('');

  const showToast = (type, text) => {
    setToast({ type, text });
    setTimeout(() => setToast(null), 3500);
  };

  // ── Load ────────────────────────────────────────────────────────────────────

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [catRes, itemRes] = await Promise.all([
        api.get('/api/categories'),
        api.get('/api/inventory/items', { params: { category_id: categoryId } }),
      ]);
      const found = catRes.data.find(c => String(c.id) === String(categoryId));
      setCategory(found || null);
      setItems(itemRes.data);
    } finally {
      setLoading(false);
    }
  }, [categoryId]);

  useEffect(() => { loadAll(); }, [loadAll]);

  // ── Open add modal ──────────────────────────────────────────────────────────

  const openAdd = async () => {
    setPhase(1);
    setSelPoId('');
    setSelEntry(null);
    setSerials([]);
    setSaving(false);
    try {
      const r = await api.get(`/api/inventory/po-stock/${categoryId}`);
      setPoStock(r.data);
    } catch {
      setPoStock([]);
    }
    setModal('add');
  };

  // Unique POs from the stock list
  const uniquePOs = [...new Map(
    poStock.map(e => [e.po_id, { po_id: e.po_id, po_number: e.po_number, vendor_name: e.vendor_name }])
  ).values()];

  const itemsForSelectedPO = poStock.filter(e => String(e.po_id) === String(selPoId));

  const selectEntry = (entry) => {
    setSelEntry(entry);
    setSerials(Array(entry.remaining).fill(''));
    setRefNumbers(Array(entry.remaining).fill(''));
    setIsManual(false);
    serialRefs.current = [];
    setPhase(2);
  };

  // Auto-focus first field on entering phase 2 in scan mode
  useEffect(() => {
    if (phase === 2) {
      setTimeout(() => serialRefs.current[0]?.focus(), 80);
    }
  }, [phase]);

  const updateSerial    = (idx, val) => setSerials(p    => p.map((s, i) => i === idx ? val : s));
  const updateRefNumber = (idx, val) => setRefNumbers(p => p.map((s, i) => i === idx ? val : s));

  const saveStock = async () => {
    if (!selEntry) return;
    setSaving(true);
    try {
      await api.post('/api/inventory/items/bulk', {
        po_item_id:     selEntry.po_item_id,
        serial_numbers: serials,
        ref_numbers:    refNumbers,
      });
      await loadAll();
      showToast('success', `${serials.filter(s => s.trim()).length} item(s) added to inventory.`);
      setModal(null);
    } catch (e) {
      showToast('error', e?.response?.data?.detail || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  // ── Migration stock ──────────────────────────────────────────────────────────

  const openAddMigration = async () => {
    setMigTypeId('');
    setMigQty(1);
    setMigSerials(['']);
    setMigRefs(['']);
    setMigSaving(false);
    try {
      const r = await api.get(`/api/categories/${categoryId}/types`);
      setMigTypes(r.data || []);
    } catch {
      setMigTypes([]);
    }
    setModal('add-migration');
  };

  const adjustMigQty = (qty) => {
    const n = Math.max(1, qty);
    setMigQty(n);
    setMigSerials(prev => Array(n).fill('').map((_, i) => prev[i] || ''));
    setMigRefs(prev   => Array(n).fill('').map((_, i) => prev[i] || ''));
  };

  const saveMigrationStock = async () => {
    const filled = migSerials.filter(s => s.trim());
    if (!filled.length) return showToast('error', 'Enter at least one serial number.');
    setMigSaving(true);
    try {
      await api.post('/api/inventory/items/migration-bulk', {
        category_id:    Number(categoryId),
        type_id:        migTypeId ? Number(migTypeId) : null,
        serial_numbers: migSerials,
        ref_numbers:    migRefs,
      });
      await loadAll();
      showToast('success', `${filled.length} migration item(s) added.`);
      setModal(null);
    } catch (e) {
      showToast('error', e?.response?.data?.detail || 'Save failed.');
    } finally {
      setMigSaving(false);
    }
  };

  // ── Edit ────────────────────────────────────────────────────────────────────

  const openEdit = (item) => {
    setSelItem(item);
    setEditForm({ serial_number: item.serial_number || '', ref_number: item.ref_number || '', status: item.status, notes: item.notes || '' });
    setModal('edit');
  };

  const saveEdit = async () => {
    setSaving(true);
    try {
      await api.put(`/api/inventory/items/${selItem.id}`, editForm);
      await loadAll();
      showToast('success', 'Item updated.');
      setModal(null);
    } catch (e) {
      showToast('error', e?.response?.data?.detail || 'Update failed.');
    } finally {
      setSaving(false);
    }
  };

  // ── Delete ──────────────────────────────────────────────────────────────────

  const confirmDelete = async () => {
    setSaving(true);
    try {
      await api.delete(`/api/inventory/items/${selItem.id}`);
      await loadAll();
      showToast('success', 'Item deleted.');
      setModal(null);
    } catch (e) {
      showToast('error', e?.response?.data?.detail || 'Delete failed.');
    } finally {
      setSaving(false);
    }
  };

  // ── Filters ──────────────────────────────────────────────────────────────────

  const filtered = items.filter(it => {
    const q = search.toLowerCase();
    return (
      (!q || it.serial_number?.toLowerCase().includes(q) || it.ref_number?.toLowerCase().includes(q) || it.item_id?.toLowerCase().includes(q) || it.type_name?.toLowerCase().includes(q) || it.po_number?.toLowerCase().includes(q))
      && (!stFilter || it.status === stFilter)
    );
  });

  const meta  = category?.ref_number_required
    ? { label: 'Serial Number', placeholder: 'Serial number' }
    : identifierMeta(category?.name);
  const title = category ? `Inventory — ${category.name}` : 'Inventory';

  // Build sets for real-time duplicate detection
  const existingSerialSet = new Set(
    items.map(i => (i.serial_number || '').trim().toLowerCase()).filter(Boolean)
  );
  const existingRefSet = new Set(
    items.map(i => (i.ref_number || '').trim().toLowerCase()).filter(Boolean)
  );

  const getDuplicateError = (val, idx) => {
    const v = val.trim().toLowerCase();
    if (!v) return null;
    if (existingSerialSet.has(v)) return 'Already exists in inventory';
    if (serials.some((s, i) => i !== idx && s.trim().toLowerCase() === v)) return 'Duplicate in this batch';
    return null;
  };

  const getRefDuplicateError = (val, idx) => {
    const v = val.trim().toLowerCase();
    if (!v) return null;
    if (existingRefSet.has(v)) return 'Already exists in inventory';
    if (refNumbers.some((r, i) => i !== idx && (r || '').trim().toLowerCase() === v)) return 'Duplicate in this batch';
    return null;
  };

  const anyDuplicates = serials.some((s, idx) => s.trim() && getDuplicateError(s, idx))
    || (category?.ref_number_required && refNumbers.some((r, idx) => r?.trim() && getRefDuplicateError(r, idx)));

  const counts = {
    total:        items.length,
    inStock:      items.filter(i => i.status === 'in-stock').length,
    dealerStock:  items.filter(i => i.status === 'dealer-stock').length,
    packed:       items.filter(i => i.status === 'packed').length,
    dispatched:   items.filter(i => i.status === 'dispatched').length,
    installed:    items.filter(i => ['installed', 'deployed'].includes(i.status)).length,
    cancelled:    items.filter(i => i.status === 'cancelled').length,
    issues:       items.filter(i => ['damaged', 'lost', 'expired', 'not_working', 'other'].includes(i.status)).length,
  };

  return (
    <Layout title={title}>

      {/* Toast */}
      {toast && (
        <div style={{
          position: 'fixed', top: '20px', right: '24px', zIndex: 4000,
          background: toast.type === 'success' ? C.successBg : C.dangerBg,
          color:      toast.type === 'success' ? C.successText : C.dangerText,
          border:     `1px solid ${toast.type === 'success' ? '#6EE7B7' : '#FCA5A5'}`,
          borderRadius: '10px', padding: '12px 20px', fontSize: '13px', fontWeight: 600,
          boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
        }}>
          {toast.type === 'success' ? '✓' : '⚠'} {toast.text}
        </div>
      )}

      {/* Stats row */}
      <div style={{ display: 'flex', gap: '12px', marginBottom: '22px', flexWrap: 'wrap', alignItems: 'center' }}>
        {/* Total */}
        <div style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: '10px', padding: '12px 16px', display: 'flex', alignItems: 'center', gap: '10px' }}>
          <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: C.primary }} />
          <div>
            <div style={{ fontSize: '20px', fontWeight: 800, color: C.textMain }}>{counts.total}</div>
            <div style={{ fontSize: '11px', color: C.textSub, fontWeight: 500 }}>Total Items</div>
          </div>
        </div>

        {/* Lifecycle pipeline chips */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexWrap: 'wrap' }}>
          {[
            { key: 'inStock',      icon: '📦', label: 'In Stock',      val: counts.inStock,      bg: '#DCFCE7', color: '#15803D', dot: '#16A34A' },
            { key: 'dealerStock',  icon: '🏪', label: 'Dealer Stock',  val: counts.dealerStock,  bg: '#F5F3FF', color: '#6D28D9', dot: '#7C3AED' },
            { key: 'packed',       icon: '🎁', label: 'Packed',        val: counts.packed,       bg: '#EDE9FE', color: '#6D28D9', dot: '#7C3AED' },
            { key: 'dispatched',   icon: '🚚', label: 'Dispatched',    val: counts.dispatched,   bg: '#DBEAFE', color: '#1D4ED8', dot: '#2563EB' },
            { key: 'installed',    icon: '✅', label: 'Installed',     val: counts.installed,    bg: '#D1FAE5', color: '#065F46', dot: '#059669' },
            ...(counts.cancelled > 0
              ? [{ key: 'cancelled', icon: '🚫', label: 'Cancelled', val: counts.cancelled, bg: '#FEE2E2', color: '#991B1B', dot: '#EF4444' }]
              : []),
          ].map(({ key, icon, label, val, bg, color, dot }, i) => (
            <React.Fragment key={key}>
              {i > 0 && (
                <span style={{ color: C.border, fontSize: '16px', fontWeight: 300, userSelect: 'none' }}>›</span>
              )}
              <div style={{
                background: val > 0 ? bg : C.primaryBg,
                border: `1px solid ${val > 0 ? dot + '55' : C.border}`,
                borderRadius: '10px', padding: '10px 14px',
                display: 'flex', alignItems: 'center', gap: '8px',
              }}>
                <span style={{ fontSize: '16px' }}>{icon}</span>
                <div>
                  <div style={{ fontSize: '18px', fontWeight: 800, color: val > 0 ? color : C.textSub }}>{val}</div>
                  <div style={{ fontSize: '10px', color: val > 0 ? color : C.textSub, fontWeight: 600, whiteSpace: 'nowrap' }}>{label}</div>
                </div>
              </div>
            </React.Fragment>
          ))}
        </div>

        {/* Issues chip */}
        {counts.issues > 0 && (
          <div style={{
            background: '#FEF3C7', border: '1px solid #FCD34D',
            borderRadius: '10px', padding: '10px 14px',
            display: 'flex', alignItems: 'center', gap: '8px',
          }}>
            <span style={{ fontSize: '16px' }}>⚠️</span>
            <div>
              <div style={{ fontSize: '18px', fontWeight: 800, color: '#92400E' }}>{counts.issues}</div>
              <div style={{ fontSize: '10px', color: '#92400E', fontWeight: 600 }}>Issues</div>
            </div>
          </div>
        )}

        {category?.migration_available && (
          <button onClick={openAddMigration} style={{
            ...btnPrimary, marginLeft: 'auto',
            background: 'linear-gradient(135deg, #7C3AED, #5B21B6)',
          }}>
            🔄 Add Migration Stock
          </button>
        )}
        <button onClick={openAdd} style={{ ...btnPrimary, ...(category?.migration_available ? {} : { marginLeft: 'auto' }) }}>+ Add Stock</button>
      </div>

      {/* Table */}
      <div style={card}>
        <div style={{ display: 'flex', gap: '10px', marginBottom: '18px', flexWrap: 'wrap', alignItems: 'center' }}>
          <input placeholder={`Search ${meta.label}${category?.ref_number_required ? ', ref no.' : ''}, item ID, type…`}
            value={search} onChange={e => setSearch(e.target.value)}
            style={{ flex: 1, minWidth: '200px', ...inputBase }} />
          <select value={stFilter} onChange={e => setStFilter(e.target.value)} style={selS}>
            <option value="">All Status</option>
            {Object.entries(STATUS_STYLE).filter(([k]) => k !== 'deployed').map(([k, v]) => (
              <option key={k} value={k}>{v.icon} {v.label}</option>
            ))}
          </select>
          {(search || stFilter) && (
            <button onClick={() => { setSearch(''); setStFilter(''); }} style={btnGhost}>Clear</button>
          )}
          <span style={{ fontSize: '12px', color: C.textSub }}>{filtered.length} / {items.length}</span>
        </div>

        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: category?.warranty_applicable ? '1000px' : '680px' }}>
            <thead>
              <tr style={{ background: C.primaryBg, borderBottom: `2px solid ${C.border}` }}>
                {[
                  'Item ID', meta.label,
                  ...(category?.ref_number_required ? ['Ref No.'] : []),
                  'Type', 'Source PO', 'Status',
                  ...(category?.warranty_applicable ? ['🛡️ Vendor Warranty', '👤 Client Warranty'] : []),
                  'Added On', 'Actions',
                ].map(h => (
                  <th key={h} style={th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(() => {
                const colSpan = 7
                  + (category?.ref_number_required ? 1 : 0)
                  + (category?.warranty_applicable ? 2 : 0);
                return loading ? (
                  <tr><td colSpan={colSpan} style={emptyCell}>Loading…</td></tr>
                ) : filtered.length === 0 ? (
                  <tr><td colSpan={colSpan} style={emptyCell}>
                    {items.length === 0
                      ? `No items yet. Click "+ Add Stock" to receive from a Purchase Order.`
                      : 'No results match your filter.'}
                  </td></tr>
                ) : filtered.map((it, i) => {
                  const ss = STATUS_STYLE[it.status] || STATUS_STYLE['in-stock'];
                  return (
                    <tr key={it.id} style={{ background: i % 2 === 0 ? C.white : '#FFFBF5', borderBottom: `1px solid ${C.border}` }}>
                      <td style={td}>
                        <span style={{ fontFamily: 'monospace', fontWeight: 700, color: C.primaryDeep, fontSize: '12px' }}>{it.item_id}</span>
                      </td>
                      <td style={td}>
                        <span style={{ fontFamily: 'monospace', fontSize: '13px', fontWeight: 600 }}>{it.serial_number || <span style={{ color: C.textSub }}>—</span>}</span>
                      </td>
                      {category?.ref_number_required && (
                        <td style={td}>
                          {it.ref_number
                            ? <span style={{ fontFamily: 'monospace', fontSize: '12px', fontWeight: 600, color: '#0369A1', background: '#F0F9FF', padding: '2px 8px', borderRadius: '6px' }}>{it.ref_number}</span>
                            : <span style={{ color: C.textSub }}>—</span>}
                        </td>
                      )}
                      <td style={td}>
                        {it.type_name
                          ? <span style={{ background: C.primaryLight, color: C.primaryDeep, padding: '2px 9px', borderRadius: '4px', fontSize: '11px', fontWeight: 600 }}>{it.type_name}</span>
                          : <span style={{ color: C.textSub }}>—</span>}
                      </td>
                      <td style={td}>
                        {it.po_number
                          ? <span style={{ fontFamily: 'monospace', fontSize: '12px', color: C.textSub }}>{it.po_number}</span>
                          : it.po_id == null
                            ? <span style={{ background: '#F5F3FF', color: '#6D28D9', border: '1px solid #DDD6FE', borderRadius: '20px', padding: '2px 10px', fontSize: '11px', fontWeight: 700 }}>🔄 Migration</span>
                            : <span style={{ color: C.textSub }}>—</span>}
                      </td>
                      <td style={td}>
                        <span style={{
                          display: 'inline-flex', alignItems: 'center', gap: '5px',
                          background: ss.bg, color: ss.color,
                          padding: '4px 11px', borderRadius: '20px', fontSize: '11px', fontWeight: 700,
                          border: `1px solid ${ss.dot || ss.color}33`,
                        }}>
                          <span style={{ fontSize: '12px', lineHeight: 1 }}>{ss.icon || '●'}</span>
                          {ss.label}
                        </span>
                        {it.linked_to && (
                          <div style={{ marginTop: '4px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <span style={{ fontSize: '10px', color: C.textSub }}>
                              {it.linked_to.type === 'mapping' ? '👤' : it.linked_to.type === 'dealer' ? '🏪' : '📦'}
                            </span>
                            <span style={{ fontSize: '10px', color: C.textSub, fontWeight: 600 }}>{it.linked_to.label}</span>
                            {it.linked_to.ref && (
                              <span style={{ fontSize: '10px', color: C.textSub, opacity: 0.7 }}>· {it.linked_to.ref}</span>
                            )}
                          </div>
                        )}
                      </td>
                      {category?.warranty_applicable && (
                        <>
                          <td style={td}><WarrantyBadge dateStr={it.vendor_warranty_end} na={!it.vendor_warranty_end} /></td>
                          <td style={td}><WarrantyBadge dateStr={it.client_warranty_end} na={!it.client_warranty_end} naLabel="NA" /></td>
                        </>
                      )}
                      <td style={td}><span style={{ fontSize: '12px', color: C.textSub }}>{it.created_at}</span></td>
                      <td style={td}>
                        <div style={{ display: 'flex', gap: '5px' }}>
                          <SmBtn label="Edit"   color="#3B82F6" onClick={() => openEdit(it)} />
                          <SmBtn label="Delete" color={C.danger} onClick={() => { setSelItem(it); setModal('delete'); }} />
                        </div>
                      </td>
                    </tr>
                  );
                });
              })()}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Add Stock Modal ── */}
      {modal === 'add' && (
        <Overlay>
          <div style={{ ...modalBox, maxWidth: phase === 1 ? '620px' : (category?.ref_number_required ? '680px' : '500px') }}>

            {/* Header */}
            <div style={{ padding: '20px 28px', borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{ width: '3px', height: '18px', background: C.primary, borderRadius: '2px' }} />
                <span style={{ fontSize: '15px', fontWeight: 700, color: C.textMain }}>
                  {phase === 1 ? `Add Stock — ${category?.name}` : `Enter ${meta.label}`}
                </span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                {/* Step indicator */}
                <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                  {[1, 2].map(s => (
                    <div key={s} style={{
                      width: s === phase ? '24px' : '8px', height: '8px', borderRadius: '4px',
                      background: s <= phase ? C.primary : C.border, transition: 'width 0.2s',
                    }} />
                  ))}
                </div>
                <button onClick={() => setModal(null)} style={{ background: 'none', border: 'none', fontSize: '18px', cursor: 'pointer', color: C.textSub }}>✕</button>
              </div>
            </div>

            <div style={{ padding: '22px 28px', overflowY: 'auto', maxHeight: '68vh' }}>

              {/* ── Phase 1: Select PO then item ── */}
              {phase === 1 && (
                <>
                  {poStock.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '40px 0', color: C.textSub }}>
                      <div style={{ fontSize: '36px', marginBottom: '12px' }}>📭</div>
                      <div style={{ fontWeight: 700, color: C.textMain, marginBottom: '6px' }}>No PO Stock Available</div>
                      <div style={{ fontSize: '13px' }}>
                        No received Purchase Orders have unused <strong>{category?.name}</strong> stock.<br />
                        Receive a PO first, then come back to add inventory.
                      </div>
                    </div>
                  ) : (
                    <>
                      {/* PO selector */}
                      <div style={{ marginBottom: '20px' }}>
                        <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: C.textMain, marginBottom: '6px' }}>
                          Select Purchase Order
                        </label>
                        <select value={selPoId} onChange={e => { setSelPoId(e.target.value); setSelEntry(null); }}
                          style={{ ...inputBase, cursor: 'pointer' }}>
                          <option value="">— Choose a received PO —</option>
                          {uniquePOs.map(p => (
                            <option key={p.po_id} value={p.po_id}>
                              {p.po_number}{p.vendor_name ? ` — ${p.vendor_name}` : ''}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Items for the selected PO */}
                      {selPoId && (
                        <div>
                          <div style={{ fontSize: '12px', fontWeight: 700, color: C.textMain, marginBottom: '10px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                            Available Stock in this PO
                          </div>
                          <div style={{ border: `1px solid ${C.border}`, borderRadius: '10px', overflow: 'hidden' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                              <thead>
                                <tr style={{ background: C.primaryBg }}>
                                  {['Type', 'Total Qty', 'Already Added', 'Remaining', ''].map(h => (
                                    <th key={h} style={{ ...th, padding: '9px 14px', fontSize: '10px' }}>{h}</th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {itemsForSelectedPO.map((e, idx) => (
                                  <tr key={e.po_item_id} style={{ borderTop: idx ? `1px solid ${C.border}` : 'none', background: C.white }}>
                                    <td style={{ padding: '12px 14px', fontWeight: 600, fontSize: '13px' }}>
                                      {e.type_name || <span style={{ color: C.textSub, fontStyle: 'italic' }}>No type</span>}
                                    </td>
                                    <td style={{ padding: '12px 14px', textAlign: 'center' }}>{e.quantity}</td>
                                    <td style={{ padding: '12px 14px', textAlign: 'center', color: C.textSub }}>{e.added}</td>
                                    <td style={{ padding: '12px 14px', textAlign: 'center' }}>
                                      <span style={{
                                        background: C.successBg, color: C.successText,
                                        padding: '2px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: 700,
                                      }}>{e.remaining}</span>
                                    </td>
                                    <td style={{ padding: '10px 14px' }}>
                                      <button onClick={() => selectEntry(e)} style={{
                                        padding: '6px 14px', borderRadius: '7px', fontSize: '12px', fontWeight: 700,
                                        background: `linear-gradient(135deg, ${C.primary}, ${C.primaryDark})`,
                                        color: '#fff', border: 'none', cursor: 'pointer',
                                      }}>
                                        Select →
                                      </button>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </>
              )}

              {/* ── Phase 2: Enter serial numbers ── */}
              {phase === 2 && selEntry && (
                <>
                  {/* Info banner + mode toggle */}
                  <div style={{
                    background: C.primaryLight, border: `1px solid ${C.primary}30`,
                    borderRadius: '10px', padding: '14px 18px', marginBottom: '16px',
                    display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                      <span style={{ fontSize: '20px' }}>📦</span>
                      <div>
                        <div style={{ fontWeight: 700, color: C.primaryDeep, fontSize: '13px', marginBottom: '3px' }}>
                          {selEntry.type_name || 'No type'} — from {selEntry.po_number}
                        </div>
                        <div style={{ fontSize: '12px', color: C.primaryDeep, opacity: 0.75 }}>
                          Enter <strong>{selEntry.remaining}</strong> {meta.label}(s) — one per unit
                        </div>
                      </div>
                    </div>
                    {/* Manual entry checkbox */}
                    <label style={{ display: 'flex', alignItems: 'center', gap: '7px', cursor: 'pointer', flexShrink: 0 }}>
                      <input
                        type="checkbox"
                        checked={isManual}
                        onChange={e => {
                          setIsManual(e.target.checked);
                          if (!e.target.checked) {
                            // switched to scan mode — focus the first unfilled field
                            const firstEmpty = serials.findIndex(s => !s.trim());
                            const focusIdx = firstEmpty === -1 ? serials.length - 1 : firstEmpty;
                            setTimeout(() => serialRefs.current[focusIdx]?.focus(), 50);
                          }
                        }}
                        style={{ width: '15px', height: '15px', cursor: 'pointer', accentColor: C.primary }}
                      />
                      <span style={{ fontSize: '12px', fontWeight: 600, color: C.primaryDeep, whiteSpace: 'nowrap' }}>
                        Manual Entry
                      </span>
                    </label>
                  </div>

                  {/* Mode hint */}
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: '8px',
                    marginBottom: '14px', padding: '8px 12px',
                    background: isManual ? C.warningBg : C.infoBg,
                    border: `1px solid ${isManual ? C.warning : C.info}30`,
                    borderRadius: '8px',
                  }}>
                    <span style={{ fontSize: '14px' }}>{isManual ? '⌨️' : '📷'}</span>
                    <span style={{ fontSize: '12px', color: isManual ? C.warningText : C.infoText, fontWeight: 500 }}>
                      {isManual
                        ? 'Manual mode — type freely, cursor stays in the same field'
                        : 'Scan mode — press Enter (or scan) to jump to the next field automatically'}
                    </span>
                  </div>

                  {/* Column labels when ref number is required */}
                  {category?.ref_number_required && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px', paddingLeft: '38px' }}>
                      <span style={{ flex: 1, minWidth: 0, fontSize: '10px', fontWeight: 700, color: C.textSub, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{meta.label}</span>
                      <span style={{ width: '160px', flexShrink: 0, fontSize: '10px', fontWeight: 700, color: '#0369A1', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Reference No. 🔢</span>
                      <span style={{ width: '24px', flexShrink: 0 }} />
                    </div>
                  )}

                  {/* Serial inputs */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {serials.map((val, idx) => {
                      const dupErr    = getDuplicateError(val, idx);
                      const filled    = val.trim().length > 0;
                      const isOk      = filled && !dupErr;
                      const refVal    = refNumbers[idx] || '';
                      const needRef   = category?.ref_number_required;
                      const refDupErr = needRef ? getRefDuplicateError(refVal, idx) : null;
                      return (
                        <div key={idx}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            {/* Circle number */}
                            <div style={{
                              width: '28px', height: '28px', borderRadius: '50%', flexShrink: 0,
                              background: dupErr
                                ? C.danger
                                : isOk
                                  ? `linear-gradient(135deg, ${C.primary}, ${C.primaryDark})`
                                  : C.border,
                              color: (dupErr || isOk) ? '#fff' : C.textSub,
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              fontSize: '11px', fontWeight: 700, transition: 'background 0.2s',
                            }}>
                              {idx + 1}
                            </div>

                            {/* IMEI / Serial input */}
                            <input
                              ref={el => serialRefs.current[idx] = el}
                              value={val}
                              onChange={e => updateSerial(idx, e.target.value)}
                              onKeyDown={e => {
                                if (!isManual && e.key === 'Enter') {
                                  e.preventDefault();
                                  const nextIdx = idx + 1 < serials.length ? idx + 1 : idx;
                                  serialRefs.current[nextIdx]?.focus();
                                }
                              }}
                              placeholder={`Unit ${idx + 1} — ${meta.placeholder}`}
                              style={{
                                flex: 1, minWidth: 0, ...inputBase,
                                fontFamily: 'monospace', fontSize: '13px',
                                borderColor: dupErr ? C.danger : isOk ? C.primary : C.border,
                                background: dupErr ? '#FFF5F5' : C.white,
                                transition: 'border-color 0.15s, background 0.15s',
                              }}
                            />

                            {/* Reference number input — only when required */}
                            {needRef && (
                              <input
                                value={refVal}
                                onChange={e => updateRefNumber(idx, e.target.value)}
                                placeholder="Ref No."
                                style={{
                                  width: '160px', flexShrink: 0,
                                  padding: '9px 10px',
                                  border: `1.5px solid ${refDupErr ? C.danger : refVal.trim() ? '#0EA5E9' : C.border}`,
                                  borderRadius: '8px', fontSize: '12px', fontFamily: 'monospace',
                                  color: C.textMain, outline: 'none', boxSizing: 'border-box',
                                  background: refDupErr ? '#FFF5F5' : refVal.trim() ? '#F0F9FF' : C.white,
                                }}
                              />
                            )}

                            {/* Status icon */}
                            <span style={{ flexShrink: 0, fontSize: '16px', width: '20px', textAlign: 'center' }}>
                              {dupErr ? '✗' : isOk ? '✓' : ''}
                            </span>
                          </div>

                          {/* Inline duplicate errors */}
                          {dupErr && (
                            <div style={{
                              marginLeft: '38px', marginTop: '3px',
                              fontSize: '11px', fontWeight: 600, color: C.danger,
                              display: 'flex', alignItems: 'center', gap: '4px',
                            }}>
                              ⚠ Serial: {dupErr}
                            </div>
                          )}
                          {refDupErr && (
                            <div style={{
                              marginLeft: '38px', marginTop: '2px',
                              fontSize: '11px', fontWeight: 600, color: C.danger,
                              display: 'flex', alignItems: 'center', gap: '4px',
                            }}>
                              ⚠ Ref No.: {refDupErr}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {/* Progress */}
                  <div style={{ marginTop: '16px', padding: '10px 16px', background: C.primaryBg, borderRadius: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: '12px', color: C.textSub }}>
                      {serials.filter(s => s.trim()).length} of {serials.length} filled
                    </span>
                    <div style={{ flex: 1, margin: '0 16px', height: '4px', background: C.border, borderRadius: '2px' }}>
                      <div style={{
                        width: `${(serials.filter(s => s.trim()).length / serials.length) * 100}%`,
                        height: '100%', background: C.primary, borderRadius: '2px', transition: 'width 0.2s',
                      }} />
                    </div>
                    <span style={{ fontSize: '12px', fontWeight: 700, color: C.primaryDeep }}>
                      {Math.round((serials.filter(s => s.trim()).length / serials.length) * 100)}%
                    </span>
                  </div>
                </>
              )}
            </div>

            {/* Footer */}
            <div style={{ padding: '14px 28px', borderTop: `1px solid ${C.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                {phase === 2 && (
                  <button onClick={() => { setPhase(1); setSelEntry(null); }} style={btnGhost}>
                    ← Back
                  </button>
                )}
              </div>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button onClick={() => setModal(null)} style={btnGhost}>Cancel</button>
                {phase === 2 && (
                  <button
                    onClick={saveStock}
                    disabled={saving || serials.filter(s => s.trim()).length === 0 || anyDuplicates}
                    style={{
                      ...btnPrimary,
                      opacity: (serials.filter(s => s.trim()).length === 0 || anyDuplicates) ? 0.5 : 1,
                      cursor: anyDuplicates ? 'not-allowed' : 'pointer',
                    }}
                    title={anyDuplicates ? 'Fix duplicate entries before saving' : ''}
                  >
                    {saving ? 'Saving…' : anyDuplicates ? '⚠ Fix Duplicates' : `💾 Save ${serials.filter(s => s.trim()).length} Item(s)`}
                  </button>
                )}
              </div>
            </div>
          </div>
        </Overlay>
      )}

      {/* ── Edit Modal ── */}
      {modal === 'edit' && selItem && (
        <Overlay>
          <div style={{ ...modalBox, maxWidth: '420px' }}>
            <MHeader title={`Edit — ${selItem.item_id}`} onClose={() => setModal(null)} />
            <div style={{ padding: '22px 28px' }}>
              <FF label={meta.label}>
                <input value={editForm.serial_number}
                  onChange={e => setEditForm(p => ({ ...p, serial_number: e.target.value }))}
                  placeholder={meta.placeholder}
                  style={{ ...inputBase, fontFamily: 'monospace' }} />
              </FF>
              {category?.ref_number_required && (
                <FF label="Reference Number">
                  <input value={editForm.ref_number}
                    onChange={e => setEditForm(p => ({ ...p, ref_number: e.target.value }))}
                    placeholder="e.g. chassis no., reg. no., licence key…"
                    style={{ ...inputBase, fontFamily: 'monospace', borderColor: editForm.ref_number ? '#0EA5E9' : undefined }} />
                </FF>
              )}
              <FF label="Status">
                {selItem.status === 'packed' ? (
                  <div style={{ padding: '8px 14px', background: '#F3E8FF', border: '1px solid #DDD6FE', borderRadius: '8px', fontSize: '12px', color: '#6D28D9', fontWeight: 600 }}>
                    📦 This item is packed in a Package — manage status from the Package module
                  </div>
                ) : selItem.status === 'replacement' ? (
                  <div style={{ padding: '8px 14px', background: '#E0F2FE', border: '1px solid #BAE6FD', borderRadius: '8px', fontSize: '12px', color: '#0369A1', fontWeight: 600 }}>
                    🔄 This item is a replacement unit in a Package — manage status from the Package module
                  </div>
                ) : selItem.status === 'installed' ? (
                  <div style={{ padding: '10px 14px', background: '#D1FAE5', border: '1px solid #6EE7B7', borderRadius: '8px', fontSize: '12px', color: '#065F46', fontWeight: 600, lineHeight: 1.5 }}>
                    ✅ This item is installed at a client — status is managed by the Product Mapping module
                    {selItem.linked_to && (
                      <div style={{ marginTop: '4px', fontWeight: 500, color: '#047857' }}>
                        Client: {selItem.linked_to.label} · {selItem.linked_to.ref}
                      </div>
                    )}
                  </div>
                ) : selItem.status === 'dispatched' ? (
                  <div style={{ padding: '10px 14px', background: '#DBEAFE', border: '1px solid #93C5FD', borderRadius: '8px', fontSize: '12px', color: '#1D4ED8', fontWeight: 600, lineHeight: 1.5 }}>
                    🚚 This item has been dispatched — status is managed by the Product Mapping module
                    {selItem.linked_to && (
                      <div style={{ marginTop: '4px', fontWeight: 500, color: '#1D4ED8' }}>
                        Package: {selItem.linked_to.label}
                      </div>
                    )}
                  </div>
                ) : selItem.status === 'cancelled' ? (
                  <div style={{ padding: '10px 14px', background: '#FEE2E2', border: '1px solid #FECACA', borderRadius: '8px', fontSize: '12px', color: '#991B1B', fontWeight: 600, lineHeight: 1.5 }}>
                    🚫 This item's renewal subscription was cancelled — restore it from the Renewal List to put the device back in service
                    {selItem.linked_to && (
                      <div style={{ marginTop: '4px', fontWeight: 500, color: '#B91C1C' }}>
                        {selItem.linked_to.label} · {selItem.linked_to.ref}
                      </div>
                    )}
                  </div>
                ) : (
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    {Object.entries(STATUS_STYLE)
                      .filter(([k]) => k !== 'packed' && k !== 'replacement' && k !== 'deployed' && k !== 'cancelled')
                      .map(([k, v]) => {
                        const active = editForm.status === k;
                        return (
                          <button key={k} onClick={() => setEditForm(p => ({ ...p, status: k }))} style={{
                            display: 'flex', alignItems: 'center', gap: '5px',
                            padding: '8px 14px', borderRadius: '8px', cursor: 'pointer',
                            fontSize: '12px', fontWeight: 700,
                            border: `2px solid ${active ? v.color : C.border}`,
                            background: active ? v.bg : C.white,
                            color: active ? v.color : C.textSub, transition: 'all 0.15s',
                          }}>
                            <span style={{ fontSize: '14px' }}>{v.icon}</span>
                            {v.label}
                          </button>
                        );
                    })}
                  </div>
                )}
              </FF>
              <FF label="Notes">
                <textarea value={editForm.notes}
                  onChange={e => setEditForm(p => ({ ...p, notes: e.target.value }))}
                  placeholder="Optional notes…" rows={3}
                  style={{ ...inputBase, resize: 'vertical', fontFamily: 'inherit' }} />
              </FF>
            </div>
            <div style={{ padding: '14px 28px', borderTop: `1px solid ${C.border}`, display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button onClick={() => setModal(null)} style={btnGhost}>Cancel</button>
              <button onClick={saveEdit} disabled={saving} style={btnPrimary}>{saving ? 'Saving…' : '💾 Update'}</button>
            </div>
          </div>
        </Overlay>
      )}

      {/* ── Delete Modal ── */}
      {modal === 'delete' && selItem && (
        <Overlay>
          <div style={{ ...modalBox, maxWidth: '380px' }}>
            <MHeader title="Remove Item" onClose={() => setModal(null)} />
            <div style={{ padding: '20px 28px' }}>
              <p style={{ margin: 0, fontSize: '13px', color: C.textMain, lineHeight: 1.6 }}>
                Remove <strong style={{ fontFamily: 'monospace', color: C.danger }}>{selItem.item_id}</strong>
                {selItem.serial_number && <> ({selItem.serial_number})</>} from inventory?
              </p>
            </div>
            <div style={{ padding: '14px 28px', borderTop: `1px solid ${C.border}`, display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button onClick={() => setModal(null)} style={btnGhost}>Cancel</button>
              <button onClick={confirmDelete} disabled={saving} style={{ ...btnPrimary, background: C.danger, boxShadow: 'none' }}>
                {saving ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </Overlay>
      )}

      {/* ── Add Migration Stock Modal ── */}
      {modal === 'add-migration' && (
        <Overlay>
          <div style={{ ...modalBox, maxWidth: category?.ref_number_required ? '620px' : '480px' }}>
            <MHeader title={`Add Migration Stock — ${category?.name}`} onClose={() => setModal(null)} />

            <div style={{ padding: '20px 28px', display: 'flex', flexDirection: 'column', gap: '18px' }}>

              {/* Info banner */}
              <div style={{ background: '#F5F3FF', border: '1px solid #DDD6FE', borderRadius: '10px', padding: '12px 16px', fontSize: '12px', color: '#5B21B6', display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                <span>🔄</span>
                <span>Migration stock is added directly without a Purchase Order. Each item gets a unique item ID.</span>
              </div>

              {/* Type selector */}
              {migTypes.length > 0 && (
                <div>
                  <label style={{ fontSize: '11px', fontWeight: 700, color: C.textSub, textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', marginBottom: '6px' }}>
                    Type <span style={{ color: C.textSub, fontWeight: 400 }}>(optional)</span>
                  </label>
                  <select
                    value={migTypeId}
                    onChange={e => setMigTypeId(e.target.value)}
                    style={{ ...selS, width: '100%' }}
                  >
                    <option value="">— Select type —</option>
                    {migTypes.map(t => (
                      <option key={t.id} value={t.id}>{t.name}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Quantity */}
              <div>
                <label style={{ fontSize: '11px', fontWeight: 700, color: C.textSub, textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', marginBottom: '6px' }}>
                  Number of Items
                </label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <button onClick={() => adjustMigQty(migQty - 1)} style={{ ...btnGhost, padding: '8px 14px', fontSize: '16px', lineHeight: 1 }}>−</button>
                  <input
                    type="number" min="1" max="50"
                    value={migQty}
                    onChange={e => adjustMigQty(Number(e.target.value))}
                    style={{ ...inputBase, width: '80px', textAlign: 'center', fontWeight: 700 }}
                  />
                  <button onClick={() => adjustMigQty(migQty + 1)} style={{ ...btnGhost, padding: '8px 14px', fontSize: '16px', lineHeight: 1 }}>+</button>
                </div>
              </div>

              {/* Serial / Ref inputs */}
              <div>
                <div style={{ display: 'grid', gridTemplateColumns: category?.ref_number_required ? '1fr 160px' : '1fr', gap: '8px', marginBottom: '8px' }}>
                  <span style={{ fontSize: '11px', fontWeight: 700, color: C.textSub, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{meta.label}</span>
                  {category?.ref_number_required && (
                    <span style={{ fontSize: '11px', fontWeight: 700, color: '#0369A1', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Reference No.</span>
                  )}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '300px', overflowY: 'auto', paddingRight: '4px' }}>
                  {migSerials.map((val, idx) => {
                    const snErr = (() => {
                      const v = val.trim().toLowerCase();
                      if (!v) return null;
                      if (existingSerialSet.has(v)) return 'Already exists';
                      if (migSerials.some((s, i) => i !== idx && s.trim().toLowerCase() === v)) return 'Duplicate in batch';
                      return null;
                    })();
                    const refVal = migRefs[idx] || '';
                    const refErr = category?.ref_number_required ? (() => {
                      const v = refVal.trim().toLowerCase();
                      if (!v) return null;
                      if (existingRefSet.has(v)) return 'Already exists';
                      if (migRefs.some((r, i) => i !== idx && (r || '').trim().toLowerCase() === v)) return 'Duplicate in batch';
                      return null;
                    })() : null;
                    return (
                      <div key={idx}>
                        <div style={{ display: 'grid', gridTemplateColumns: category?.ref_number_required ? '1fr 160px' : '1fr', gap: '8px', alignItems: 'center' }}>
                          <input
                            value={val}
                            onChange={e => setMigSerials(p => p.map((s, i) => i === idx ? e.target.value : s))}
                            placeholder={`Unit ${idx + 1} — ${meta.placeholder}`}
                            style={{
                              ...inputBase, fontFamily: 'monospace', fontSize: '13px',
                              borderColor: snErr ? C.danger : val.trim() ? C.primary : C.border,
                              background: snErr ? '#FFF5F5' : C.white,
                            }}
                          />
                          {category?.ref_number_required && (
                            <input
                              value={refVal}
                              onChange={e => setMigRefs(p => p.map((r, i) => i === idx ? e.target.value : r))}
                              placeholder="Ref No."
                              style={{
                                ...inputBase, fontFamily: 'monospace', fontSize: '12px',
                                borderColor: refErr ? C.danger : refVal.trim() ? '#0EA5E9' : C.border,
                                background: refErr ? '#FFF5F5' : refVal.trim() ? '#F0F9FF' : C.white,
                              }}
                            />
                          )}
                        </div>
                        {snErr && <div style={{ marginTop: '3px', fontSize: '11px', fontWeight: 600, color: C.danger }}>⚠ Serial: {snErr}</div>}
                        {refErr && <div style={{ marginTop: '2px', fontSize: '11px', fontWeight: 600, color: C.danger }}>⚠ Ref No.: {refErr}</div>}
                      </div>
                    );
                  })}
                </div>
                <div style={{ marginTop: '10px', fontSize: '12px', color: C.textSub }}>
                  {migSerials.filter(s => s.trim()).length} of {migQty} filled
                </div>
              </div>
            </div>

            <div style={{ padding: '14px 28px', borderTop: `1px solid ${C.border}`, display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button onClick={() => setModal(null)} style={btnGhost}>Cancel</button>
              <button
                onClick={saveMigrationStock}
                disabled={migSaving || !migSerials.some(s => s.trim())}
                style={{ ...btnPrimary, background: 'linear-gradient(135deg, #7C3AED, #5B21B6)', boxShadow: '0 3px 10px rgba(124,58,237,0.3)', opacity: migSaving || !migSerials.some(s => s.trim()) ? 0.6 : 1 }}
              >
                {migSaving ? 'Saving…' : `🔄 Add ${migSerials.filter(s => s.trim()).length || ''} Item(s)`}
              </button>
            </div>
          </div>
        </Overlay>
      )}
    </Layout>
  );
}

// ── Sub-components ────────────────────────────────────────────────────────────

function WarrantyBadge({ dateStr, na, naLabel = '—' }) {
  if (na || !dateStr) {
    return (
      <span style={{
        display: 'inline-block', padding: '3px 10px', borderRadius: '20px',
        fontSize: '11px', fontWeight: 700,
        background: '#F3F4F6', color: '#9CA3AF', border: '1px solid #E5E7EB',
      }}>{naLabel}</span>
    );
  }
  const today    = new Date(); today.setHours(0, 0, 0, 0);
  const end      = new Date(dateStr);
  const diffDays = Math.ceil((end - today) / 86400000);
  const fmtDate  = end.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });

  const expired  = diffDays < 0;
  const expiring = !expired && diffDays <= 30;

  const styles = expired
    ? { bg: '#FEE2E2', color: '#991B1B', border: '#FECACA' }
    : expiring
      ? { bg: '#FEF3C7', color: '#92400E', border: '#FCD34D' }
      : { bg: '#F0FDF4', color: '#15803D', border: '#BBF7D0' };

  const pillText = expired ? 'Expired' : expiring ? `${diffDays}d left` : fmtDate;

  return (
    <div>
      <span style={{
        display: 'inline-block', padding: '3px 9px', borderRadius: '20px',
        fontSize: '11px', fontWeight: 700,
        background: styles.bg, color: styles.color, border: `1px solid ${styles.border}`,
        whiteSpace: 'nowrap',
      }}>
        🛡️ {pillText}
      </span>
      {(expired || expiring) && (
        <div style={{ fontSize: '10px', color: '#6B7280', marginTop: '2px' }}>{fmtDate}</div>
      )}
    </div>
  );
}

function SmBtn({ label, color, onClick }) {
  return (
    <button onClick={onClick} style={{
      padding: '4px 10px', borderRadius: '6px', fontSize: '11px', fontWeight: 600,
      background: `${color}18`, color, border: `1px solid ${color}30`, cursor: 'pointer',
    }}>{label}</button>
  );
}

function FF({ label, children }) {
  return (
    <div style={{ marginBottom: '14px' }}>
      <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: C.textMain, marginBottom: '6px' }}>{label}</label>
      {children}
    </div>
  );
}

function Overlay({ children }) {
  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(28,20,7,0.65)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1000, padding: '16px',
    }}>{children}</div>
  );
}

function MHeader({ title, onClose }) {
  return (
    <div style={{ padding: '20px 28px', borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        <div style={{ width: '3px', height: '18px', background: C.primary, borderRadius: '2px' }} />
        <span style={{ fontSize: '15px', fontWeight: 700, color: C.textMain }}>{title}</span>
      </div>
      <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '18px', cursor: 'pointer', color: C.textSub }}>✕</button>
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────

const inputBase = { width: '100%', padding: '9px 12px', border: `1.5px solid ${C.border}`, borderRadius: '8px', fontSize: '13px', color: C.textMain, outline: 'none', boxSizing: 'border-box', background: C.white };
const card      = { background: C.white, borderRadius: '12px', padding: '22px', border: `1px solid ${C.border}` };
const modalBox  = { background: C.white, borderRadius: '16px', width: '100%', boxShadow: '0 24px 80px rgba(0,0,0,0.3)', border: `1px solid ${C.border}` };
const th        = { textAlign: 'left', padding: '11px 14px', fontSize: '11px', fontWeight: 700, color: C.textSub, textTransform: 'uppercase', letterSpacing: '0.5px' };
const td        = { padding: '13px 14px', fontSize: '13px', color: C.textMain, verticalAlign: 'middle' };
const emptyCell = { textAlign: 'center', padding: '52px', color: C.textSub, fontSize: '13px' };
const selS      = { padding: '9px 12px', border: `1.5px solid ${C.border}`, borderRadius: '8px', fontSize: '13px', outline: 'none', background: C.white, cursor: 'pointer' };
const btnPrimary = { padding: '10px 20px', background: `linear-gradient(135deg, ${C.primary}, ${C.primaryDark})`, color: '#fff', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: 700, cursor: 'pointer', boxShadow: `0 3px 10px rgba(245,166,35,0.3)` };
const btnGhost   = { padding: '10px 18px', background: C.primaryBg, color: C.textSub, border: `1px solid ${C.border}`, borderRadius: '8px', fontSize: '13px', fontWeight: 600, cursor: 'pointer' };
